import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';

import { BusinessUnitsService } from 'src/app/services/business-units.service';
import { BusinessUnit, BusinessUnitSummary } from '@models/business-unit.interface';

import { BusinessUnitListComponent } from './business-unit-list/business-unit-list.component';
import { BusinessUnitDetailComponent, BusinessUnitSavedEvent } from './business-unit-detail/business-unit-detail.component';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '@shared/components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-business-units-page',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    BusinessUnitListComponent,
    BusinessUnitDetailComponent
  ],
  templateUrl: './business-units-page.component.html',
  styleUrl: './business-units-page.component.scss'
})
export class BusinessUnitsPageComponent implements OnInit {
  private readonly businessUnitsService = inject(BusinessUnitsService);
  private readonly dialog = inject(MatDialog);

  protected businessUnits = signal<BusinessUnitSummary[]>([]);
  protected selectedBusinessUnit = signal<BusinessUnit | null>(null);
  protected selectedId = signal<number | null>(null);
  protected newlyCreatedId = signal<number | null>(null);
  protected isLoadingList = signal(false);
  protected isLoadingDetail = signal(false);

  ngOnInit(): void {
    this.loadList();
  }

  private loadList(selectId?: number, forceRefresh = false): void {
    this.isLoadingList.set(true);
    this.businessUnitsService.getList(forceRefresh).subscribe({
      next: list => {
        this.businessUnits.set(list);
        this.isLoadingList.set(false);

        // Re-select after refresh, or restore previous selection
        const targetId = selectId ?? this.selectedId();
        if (targetId !== null) {
          const stillPresent = list.find(b => b.id === targetId);
          if (stillPresent) {
            this.loadDetail(targetId, forceRefresh && selectId === targetId);
          } else {
            this.selectedBusinessUnit.set(null);
            this.selectedId.set(null);
          }
        }
      },
      error: () => {
        this.isLoadingList.set(false);
      }
    });
  }

  private loadDetail(id: number, forceRefresh = false): void {
    this.isLoadingDetail.set(true);
    this.selectedId.set(id);
    this.businessUnitsService.getById(id, forceRefresh).subscribe({
      next: businessUnit => {
        this.selectedBusinessUnit.set(businessUnit);
        this.isLoadingDetail.set(false);
      },
      error: () => {
        this.selectedBusinessUnit.set(null);
        this.isLoadingDetail.set(false);
      }
    });
  }

  protected onBusinessUnitSelected(summary: BusinessUnitSummary): void {
    this.newlyCreatedId.set(null);
    this.loadDetail(summary.id);
  }

  protected onAddClicked(): void {
    this.newlyCreatedId.set(null);
    // Create an unsaved "new" business unit shell
    const newBusinessUnit: BusinessUnit = {
      id: 0,
      name: '',
      alias: null,
      assetClassId: null,
      assetClassName: null,
      applyDeepwaterFormat: false,
      createdOn: new Date().toISOString()
    };
    this.selectedBusinessUnit.set(newBusinessUnit);
    this.selectedId.set(null);
  }

  protected onBusinessUnitSaved(event: BusinessUnitSavedEvent): void {
    // Reload the list and re-select the saved business unit
    this.selectedId.set(event.id);
    this.newlyCreatedId.set(event.id);
    this.loadList(event.id, true);
  }

  protected onBusinessUnitDeleted(businessUnitId: number): void {
    const dialogRef = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData, boolean>(
      ConfirmationDialogComponent,
      {
        data: {
          title: 'Delete Business Unit',
          primaryText: 'Are you sure you want to delete this business unit?',
          confirmText: 'Delete',
        },
        width: '420px',
        disableClose: true,
      }
    );
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.businessUnitsService.delete(businessUnitId).subscribe({
        next: () => {
          // Clear selection if the deleted business unit was selected
          if (this.selectedId() === businessUnitId) {
            this.selectedBusinessUnit.set(null);
            this.selectedId.set(null);
          }
          // Reload the list
          this.loadList();
        },
        error: () => {
          alert('Failed to delete business unit. Please try again.');
        }
      });
    });
  }
}
